import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ArrowPathIcon, ChevronDownIcon, ChevronRightIcon, ExclamationTriangleIcon, MagnifyingGlassIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { pollTestRunStatus, fetchTestResults, fetchTenants, refreshIpEnrichment, resolveDirectoryNames } from '../api/client';
import DevicePosturePanel from './DevicePosturePanel';
import Pagination from './Pagination';
import CopyButton from './CopyButton';
import SourceEventRenderer from './sourceEvents/SourceEventRenderer';
import RawEventJsonPanel from './sourceEvents/RawEventJsonPanel';
import SourceEventsPanel from './sourceEvents/SourceEventsPanel';
import AggregationSummary from './sourceEvents/AggregationSummary';
import { parseAggregationFields } from './sourceEvents/aggregationHelpers';
import { parseInboxRuleParameters } from './sourceEvents/exchangeRuleHelpers';
import AlertFactsPanel from './sourceEvents/AlertFactsPanel';
import { parseAlertFacts, alertFactsUserCell } from './sourceEvents/alertFactsHelpers';
import { parseSecurityAlert, topEvidenceHighlight, securityAlertIpEvidence } from './sourceEvents/securityEvidenceHelpers';
import SigninOutcomeBanner from './alerts/SigninOutcomeBanner';
import LinkedAuditEventPanel from './alerts/LinkedAuditEventPanel';
import { TIMEZONE_OPTIONS, TZ_STORAGE_KEY, getDefaultTimezone, getTzOffset, resolveIana } from './TimelineView';
import { normalizeIsoUtc } from '../utils/timeFormat';
import { collectTargetResourceGuids, ResolvedId } from '../utils/directoryNames';
import { resolveBaselineComparison } from '../utils/baselineComparison';
import BaselineComparisonPanel from './alerts/BaselineComparisonPanel';
import UnfamiliarPropertiesPanel from './alerts/UnfamiliarPropertiesPanel';
import BaselineIpListDrawer from './BaselineIpListDrawer';
import { isDemoMode } from '../utils/demoMode';

// Demo Mode hides the IP-enrichment refresh control (ProxyCheck quota + a MERGE
// into the shared Delta table) — see api/client.js DEMO_READ_POSTS.
const DEMO = isDemoMode();

const POLL_INTERVAL_MS = 3000;

// Strip port/brackets from IPv4:port or [IPv6]:port notation.
function stripPort(ipPort) {
  if (!ipPort) return '';
  let ip = ipPort;
  if (ip.startsWith('[')) {
    const close = ip.indexOf(']');
    if (close !== -1) return ip.slice(1, close);
  }
  const colon = ip.lastIndexOf(':');
  if (colon !== -1) {
    const maybePart = ip.slice(colon + 1);
    if (/^\d+$/.test(maybePart) && ip.indexOf(':') === colon) return ip.slice(0, colon);
  }
  return ip;
}
const PAGE_SIZES = [25, 50, 100];

// Exchange app-only auth events populate user_id with a PUID (S-1-...), not a UPN.
// For display only, substitute the resolved app identity. Raw user_id is preserved
// on the row and on hover (title attr) for search, filter, and technical drill-down.
// For aggregation rules, prefer delegate_upn (the actual actor, resolved by the
// detection pipeline from AADSessionId) when present; fall back to mailbox_owner_upn
// with an "(owner)" suffix so it's clear the identity shown is the alert trigger.
const displayActor = (item) => {
  // A local group change names both sides in its facts (parity with AlertsPage).
  const factsCell = alertFactsUserCell(item?.alertFactsJson);
  if (factsCell) return factsCell.display;
  const uid = item?.userId;
  const agg = parseAggregationFields(item?.rawEventJson);
  if (agg) {
    if (agg.delegate_upn) return agg.delegate_upn;
    const ownerUpn = agg.mailbox_owner_upn ||
      (Array.isArray(agg.set_MailboxOwnerUPN) && agg.set_MailboxOwnerUPN.length > 0
        ? agg.set_MailboxOwnerUPN[0] : null);
    if (ownerUpn) return `${ownerUpn} (owner)`;
  }
  if (!uid) return '';
  if (uid.startsWith('S-1-')) {
    const name = item.appDisplayName || item.applicationId;
    return name ? `App: ${name}` : uid;
  }
  return uid;
};

// Low-signal modifiedProperties suppressed from the Target Resource panel.
// Role.WellKnownObjectName is still read internally to decorate Role.DisplayName.
// ConsentAction.Permissions is a packed blob parsed separately into the Permissions Granted panel.
const HIDDEN_MODIFIED_PROPS = new Set([
  'Role.ObjectID',
  'Role.TemplateId',
  'Role.WellKnownObjectName',
  'TargetId.ServicePrincipalNames',
  'ConsentAction.Permissions',
]);

// ── JSON diff utilities for Modified Properties (e.g., ConditionalAccessPolicy) ─

const DIFF_SKIP_ROOTS = new Set(['id', 'createdDateTime', 'modifiedDateTime', 'displayName']);

function tryParseAsObject(v) {
  if (!v) return null;
  let val = v;
  for (let i = 0; i < 3 && typeof val === 'string'; i++) {
    try { val = JSON.parse(val); } catch { return null; }
  }
  return (val && typeof val === 'object' && !Array.isArray(val)) ? val : null;
}

function flattenObj(obj, prefix = '') {
  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (value !== null && value !== undefined && typeof value === 'object' && !Array.isArray(value)) {
      Object.assign(result, flattenObj(value, path));
    } else {
      result[path] = value;
    }
  }
  return result;
}

function serVal(v) {
  if (v === null || v === undefined) return 'null';
  if (typeof v === 'string') return v;
  return JSON.stringify(v);
}

function computeJsonDiff(oldObj, newObj) {
  const oldFlat = flattenObj(oldObj);
  const newFlat = flattenObj(newObj);
  const allPaths = new Set([...Object.keys(oldFlat), ...Object.keys(newFlat)]);
  const changed = [], added = [], removed = [];

  for (const path of allPaths) {
    if (DIFF_SKIP_ROOTS.has(path.split('.')[0])) continue;
    const inOld = path in oldFlat, inNew = path in newFlat;
    if (inOld && inNew) {
      if (serVal(oldFlat[path]) !== serVal(newFlat[path])) {
        if (Array.isArray(oldFlat[path]) && Array.isArray(newFlat[path])) {
          const os = new Set(oldFlat[path].map(serVal));
          const ns = new Set(newFlat[path].map(serVal));
          const ai = newFlat[path].filter(x => !os.has(serVal(x)));
          const ri = oldFlat[path].filter(x => !ns.has(serVal(x)));
          if (ai.length || ri.length) changed.push({ path, type: 'array', addedItems: ai, removedItems: ri });
        } else {
          changed.push({ path, type: 'value', oldVal: oldFlat[path], newVal: newFlat[path] });
        }
      }
    } else if (inNew) {
      added.push({ path, value: newFlat[path] });
    } else {
      removed.push({ path, value: oldFlat[path] });
    }
  }
  return { changed, added, removed };
}

// True when a modifiedProperty side carries no real value — covers actual null,
// empty string, and the literal "null" string Microsoft writes on create/delete.
function isNullishVal(v) {
  if (v === null || v === undefined || v === '') return true;
  if (typeof v === 'string') { const t = v.trim(); return t === '' || t === 'null'; }
  return false;
}

function diffVal(v) {
  if (v === null || v === undefined) return 'null';
  if (typeof v === 'boolean' || typeof v === 'number') return String(v);
  if (typeof v === 'string') return v.length > 80 ? v.slice(0, 77) + '…' : v;
  if (Array.isArray(v)) {
    if (v.length === 0) return '[]';
    const items = v.map(x => typeof x === 'string' ? x : JSON.stringify(x));
    const joined = items.join(', ');
    return joined.length > 120 ? `[${v.length} items]` : `[${joined}]`;
  }
  return JSON.stringify(v);
}

// Walk "[[entry], [entry], ...]" and return each inner entry as a parsed k→v object.
// Entry format (Microsoft audit formatter, not JSON):
//   "Id: ..., ClientId: ..., PrincipalId: , ResourceId: <guid>, ConsentType: AllPrincipals,
//    Scope: <space-separated scopes>, CreatedDateTime: , LastModifiedDateTime"
function parseConsentEntryList(listText) {
  if (!listText) return [];
  let depth = 0;
  let start = -1;
  const rawEntries = [];
  for (let i = 0; i < listText.length; i++) {
    const ch = listText[i];
    if (ch === '[') {
      depth++;
      if (depth === 2) start = i + 1;
    } else if (ch === ']') {
      if (depth === 2 && start >= 0) {
        rawEntries.push(listText.slice(start, i));
        start = -1;
      }
      depth--;
    }
  }
  return rawEntries.map(entry => {
    const obj = {};
    const pairs = entry.split(/,\s+(?=[A-Za-z][A-Za-z0-9]*:\s)/);
    for (const p of pairs) {
      const m = p.match(/^\s*([A-Za-z][A-Za-z0-9]*):\s*(.*?)\s*$/);
      if (m) obj[m[1]] = m[2];
    }
    return obj;
  });
}

// Parse the two shapes Microsoft uses for consent-to-app events. Each returned
// permission is tagged with status: 'new' | 'existing' | 'revoked'.
// Shape A (_correlated_events): every perm is 'new' (child events are literal Adds).
// Shape B (ConsentAction.Permissions blob): diff OLD vs NEW by (ResourceId, scope).
function extractConsentPermissions(rawEvent) {
  if (!rawEvent) return [];
  const strip = (v) => {
    if (v === null || v === undefined) return '';
    if (typeof v !== 'string') return String(v);
    try { const parsed = JSON.parse(v); return typeof parsed === 'string' ? parsed : v; }
    catch { return v; }
  };

  // ── Shape A: correlated child events ─────────────────────────────────────
  const correlated = rawEvent._correlated_events;
  if (Array.isArray(correlated) && correlated.length > 0) {
    const perms = [];
    for (const evt of correlated) {
      const activity = evt?.activityDisplayName;
      const targets = evt?.targetResources;
      if (!Array.isArray(targets) || targets.length === 0) continue;
      const apiTarget = targets.find(t => Array.isArray(t.modifiedProperties) && t.modifiedProperties.length > 0) || targets[0];
      const apiName = apiTarget?.displayName || '';
      const props = apiTarget?.modifiedProperties || [];
      const getProp = (name) => {
        const p = props.find(x => x.displayName === name);
        return p ? strip(p.newValue) : '';
      };
      if (activity === 'Add app role assignment to service principal') {
        const scope = getProp('AppRole.Value');
        const description = getProp('AppRole.DisplayName');
        if (scope) perms.push({ kind: 'Application', api: apiName, scope, description, status: 'new' });
      } else if (activity === 'Add delegated permission grant') {
        const scope = getProp('DelegatedPermissionGrant.Scope');
        const consentType = getProp('DelegatedPermissionGrant.ConsentType');
        if (scope) {
          for (const s of scope.split(/\s+/).filter(Boolean)) {
            perms.push({ kind: 'Delegated', api: apiName, scope: s, consentType, status: 'new' });
          }
        }
      }
    }
    if (perms.length > 0) return perms;
  }

  // ── Shape B: ConsentAction.Permissions blob on the parent event ──────────
  const targets = rawEvent.targetResources;
  if (!Array.isArray(targets)) return [];
  const perms = [];
  for (const t of targets) {
    const mp = (t?.modifiedProperties || []).find(p => p.displayName === 'ConsentAction.Permissions');
    if (!mp) continue;
    const newText = strip(mp.newValue);
    const oldText = strip(mp.oldValue);
    if (!newText && !oldText) continue;

    const splitList = (text) => {
      if (!text) return '';
      const arrowIdx = text.lastIndexOf(' => ');
      if (arrowIdx < 0) return text;
      return { old: text.slice(0, arrowIdx), new: text.slice(arrowIdx + 4) };
    };

    let oldPart = '';
    let newPart = '';
    const splitFromNew = splitList(newText);
    if (typeof splitFromNew === 'object') {
      oldPart = splitFromNew.old;
      newPart = splitFromNew.new;
    } else {
      newPart = splitFromNew;
    }
    if (!oldPart && oldText) {
      const splitFromOld = splitList(oldText);
      oldPart = typeof splitFromOld === 'object' ? splitFromOld.new : splitFromOld;
    }

    const oldEntries = parseConsentEntryList(oldPart);
    const newEntries = parseConsentEntryList(newPart);

    const oldKey = (resourceId, scope) => `${resourceId}|${scope}`;
    const oldSet = new Set();
    for (const e of oldEntries) {
      const resourceId = e.ResourceId || '';
      for (const s of (e.Scope || '').split(/\s+/).filter(Boolean)) {
        oldSet.add(oldKey(resourceId, s));
      }
    }

    const newKeys = new Set();
    for (const e of newEntries) {
      const resourceId = e.ResourceId || '';
      const consentType = e.ConsentType || '';
      for (const s of (e.Scope || '').split(/\s+/).filter(Boolean)) {
        const k = oldKey(resourceId, s);
        newKeys.add(k);
        perms.push({
          kind: 'Delegated',
          api: '',
          scope: s,
          consentType,
          status: oldSet.has(k) ? 'existing' : 'new',
        });
      }
    }
    for (const e of oldEntries) {
      const resourceId = e.ResourceId || '';
      const consentType = e.ConsentType || '';
      for (const s of (e.Scope || '').split(/\s+/).filter(Boolean)) {
        const k = oldKey(resourceId, s);
        if (!newKeys.has(k)) {
          perms.push({ kind: 'Delegated', api: '', scope: s, consentType, status: 'revoked' });
        }
      }
    }
  }
  return perms;
}

// Generic collapsible wrapper for structured (non-JSON) content.
function CollapsibleSection({ title, titleClass = 'text-gray-400', defaultCollapsed = true, children }) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  return (
    <div>
      <div
        className="flex items-center gap-1.5 cursor-pointer select-none"
        onClick={(e) => { e.stopPropagation(); setCollapsed(c => !c); }}
      >
        {collapsed
          ? <ChevronRightIcon className="h-3 w-3 text-gray-500 flex-shrink-0" />
          : <ChevronDownIcon className="h-3 w-3 text-gray-500 flex-shrink-0" />}
        <span className={`text-xs ${titleClass} font-semibold uppercase tracking-wide`}>{title}</span>
      </div>
      {!collapsed && <div className="mt-2">{children}</div>}
    </div>
  );
}

function formatDateTime(dt, iana) {
  if (!dt) return '';
  try {
    // normalizeIsoUtc: append 'Z' when no zone is present (Databricks writes UTC) and
    // truncate >3-digit fractional seconds, which Firefox/Safari reject as Invalid Date
    // (the catch below would then render the raw, un-converted string).
    const d = new Date(normalizeIsoUtc(dt));
    // 'Local' carries an empty iana — resolve to the browser zone. Only show the
    // legacy …Z shape for genuine UTC (explicit UTC, or Local in a UTC browser).
    const resolved = resolveIana(iana);
    if (resolved === 'UTC') {
      return d.toISOString().replace('T', ' ').substring(0, 19) + 'Z';
    }
    return d.toLocaleString('en-US', {
      timeZone: resolved,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    });
  } catch { return dt; }
}

// ── Auth App Policy Evaluation — see AlertsPage.jsx for Microsoft Graph beta enum reference ──
const EVAL_LABELS = { success: 'success', failure: 'failure', unknownFutureValue: 'unknown' };
const STATUS_LABELS = {
  unknown: 'unknown',
  appLockOutOfDate: 'app lock out of date',
  appLockEnabled: 'app lock enabled',
  appLockDisabled: 'app lock disabled',
  appContextOutOfDate: 'app context out of date',
  appContextShown: 'app context shown',
  appContextNotShown: 'app context not shown',
  locationContextOutOfDate: 'location context out of date',
  locationContextShown: 'location context shown',
  locationContextNotShown: 'location context not shown',
  numberMatchOutOfDate: 'number match out of date',
  numberMatchCorrectNumberEntered: 'correct number entered',
  numberMatchIncorrectNumberEntered: 'incorrect number entered',
  numberMatchDeny: 'denied',
  tamperResistantHardwareOutOfDate: 'tamper-resistant hardware out of date',
  tamperResistantHardwareUsed: 'tamper-resistant hardware used',
  tamperResistantHardwareNotUsed: 'tamper-resistant hardware not used',
  unknownFutureValue: 'unknown',
};
function camelToSentence(s) {
  if (!s) return '';
  const spaced = s.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
function humanizePolicyName(name) {
  if (!name) return 'Policy';
  if (/[A-Z]/.test(name) && /[a-z]/.test(name) && !name.includes(' ')) return camelToSentence(name);
  return name;
}
function humanizePolicyEval(name, evaluation) {
  const policy = humanizePolicyName(name);
  const evalLabel = EVAL_LABELS[evaluation] || (evaluation || 'unknown').toLowerCase();
  return `${policy} ${evalLabel}`;
}
function humanizePolicyStatus(status) {
  if (!status) return null;
  return STATUS_LABELS[status] || camelToSentence(status).toLowerCase();
}
function evalTextColor(evaluation) {
  // Deliberately do NOT colour success green — see AlertsPage.jsx for rationale.
  const v = (evaluation || '').toLowerCase();
  if (v === 'failure') return 'text-red-300';
  return 'text-gray-300';
}

// Defang a URL — see AlertsPage.jsx for the full rationale.
function defangUrl(url) {
  if (!url) return url;
  try {
    const u = new URL(url);
    const host = u.hostname;
    const lastDot = host.lastIndexOf('.');
    if (lastDot < 0) return url;
    const defangedHost = host.slice(0, lastDot) + '[.]' + host.slice(lastDot + 1);
    return url.replace(host, defangedHost);
  } catch {
    const protoEnd = url.indexOf('://');
    const start = protoEnd >= 0 ? protoEnd + 3 : 0;
    const slash = url.indexOf('/', start);
    const hostEnd = slash > 0 ? slash : url.length;
    const host = url.slice(start, hostEnd);
    const lastDot = host.lastIndexOf('.');
    if (lastDot < 0) return url;
    return url.slice(0, start + lastDot) + '[.]' + url.slice(start + lastDot + 1);
  }
}

export default function TestRunResultsPage() {
  const { ruleId, jobId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Metadata passed from TestRunModal via navigate state
  const meta = location.state || {};
  const ruleName = meta.ruleName || ruleId;
  const ruleType = meta.ruleType || null;
  const parametersJson = meta.parametersJson || null;
  const dateRange = meta.startDate && meta.endDate ? `${meta.startDate} to ${meta.endDate}` : '';
  const tenantLabel = meta.tenantId || 'All tenants';
  const testRunId = meta.testRunId || null;
  const viewOnly = meta.viewOnly || false;

  // Parse rule parameters once for deriving match reasons
  const ruleParams = (() => {
    try { return parametersJson ? JSON.parse(parametersJson) : {}; } catch { return {}; }
  })();

  // Render match reason as pills (ip_flag_match) or text (other types) for the table cell
  function renderMatchReason(row) {
    if (ruleType === 'ip_flag_match') {
      const detections = (row.ipDetections || '').split(',').map(d => d.trim()).filter(Boolean);
      const ip       = row.clientIp   || '';
      const provider = row.ipProvider || '';
      const country  = row.ipCountry  || '';
      const risk     = row.ipRiskLevel != null ? row.ipRiskLevel : null;

      if (detections.length > 0 || provider) {
        // Build a sentence: "IP x.x.x.x identified as vpn + anonymous proxy by Provider (Country) · Risk: 50/100"
        const parts = [];
        if (ip)                  parts.push(`IP ${ip}`);
        if (detections.length)   parts.push(`identified as ${detections.join(' + ')}`);
        if (provider || country) parts.push(`by ${[provider, country].filter(Boolean).join(', ')}`);
        if (risk != null)        parts.push(`Risk: ${risk}/100`);
        return <span className="text-orange-300 text-xs">{parts.join(' · ')}</span>;
      }
      const flag = ruleParams.ip_flag || '';
      return <span className="text-gray-500 text-xs italic">{flag ? `IP flag: ${flag}` : '—'}</span>;
    }
    const reason = getMatchReason(row);
    return <span className="text-gray-400 text-xs">{reason || '—'}</span>;
  }

  // Derive a human-readable match reason for a result row
  function getMatchReason(row) {
    if (ruleType === 'ip_flag_match') {
      const detections = (row.ipDetections || '').split(',').map(d => d.trim()).filter(Boolean);
      const parts = [];
      if (row.clientIp)           parts.push(`IP ${row.clientIp}`);
      if (detections.length)      parts.push(`identified as ${detections.join(' + ')}`);
      const loc = [row.ipProvider, row.ipCountry].filter(Boolean).join(', ');
      if (loc)                    parts.push(`by ${loc}`);
      if (row.ipRiskLevel != null) parts.push(`Risk: ${row.ipRiskLevel}/100`);
      return parts.length > 0 ? parts.join(' · ') : (ruleParams.ip_flag ? `IP flag: ${ruleParams.ip_flag}` : 'IP threat flag');
    }
    if (ruleType === 'log_type_match') {
      const parts = [];
      if (ruleParams.log_type) parts.push(`log_type: ${ruleParams.log_type}`);
      const conds = ruleParams.match_conditions || [];
      conds.forEach(c => {
        if (c.field && c.value) parts.push(`${c.field} ${c.operator || '='} "${c.value}"`);
      });
      return parts.join(' · ') || row.logType || 'log_type match';
    }
    if (ruleType === 'baseline_deviation' || ruleType === 'operation_baseline_deviation') {
      const field = ruleType === 'operation_baseline_deviation' ? 'signin_ip_list' : (ruleParams.baseline_field || '');
      const label = field === 'country_list' ? 'country' : 'IP';
      const actual = row.baselineActualValue || '—';
      if (!row.baselineHasBaseline || row.baselineHasBaseline === 'false') return `No baseline — ${label}: ${actual}`;
      if (!row.baselineExpectedValues) return `Baseline empty — ${label}: ${actual}`;
      return `New ${label}: ${actual}`;
    }
    // Fallback: show whatever evidence is populated so unknown/future rule types
    // automatically surface something useful without requiring a frontend code change.
    const parts = [];
    const detections = (row.ipDetections || '').split(',').filter(Boolean);
    if (detections.length > 0) parts.push(detections.map(d => d.trim()).join(', '));
    if (row.baselineActualValue) {
      const field = ruleParams.baseline_field || 'value';
      const label = field === 'country_list' ? 'country' : field === 'signin_ip_list' ? 'IP' : field;
      parts.push(`new ${label}: ${row.baselineActualValue}`);
    }
    return parts.length > 0 ? parts.join(' · ') : '—';
  }

  // Phase: 'running' | 'completed' | 'error' | 'spark_error'
  const [phase, setPhase] = useState('running');
  const [statusMessage, setStatusMessage] = useState(viewOnly ? 'Loading results...' : 'Starting test run...');
  const [errorMessage, setErrorMessage] = useState(null);
  const [results, setResults] = useState([]);
  const [matchCount, setMatchCount] = useState(null);
  const [runStartTime, setRunStartTime] = useState(null);
  const [runEndTime, setRunEndTime] = useState(null);
  const pollTimerRef = useRef(null);

  // Elapsed + estimate state
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [estimateSeconds, setEstimateSeconds] = useState(null);
  const [warehouseRunning, setWarehouseRunning] = useState(null);
  const runStartRef = useRef(null);       // wall-clock time the run started (from job.startTime)
  const elapsedTimerRef = useRef(null);

  function formatElapsed(secs) {
    if (secs < 60) return `${secs}s`;
    return `${Math.floor(secs / 60)}m ${secs % 60}s`;
  }

  function startElapsedTimer(jobStartTime) {
    if (elapsedTimerRef.current) return; // already running
    // Seed elapsed from job.startTime if available so it accounts for time before first poll
    if (jobStartTime) {
      runStartRef.current = new Date(jobStartTime).getTime();
    } else {
      runStartRef.current = runStartRef.current || Date.now();
    }
    elapsedTimerRef.current = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - runStartRef.current) / 1000));
    }, 1000);
  }

  function stopElapsedTimer() {
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }
  }

  // Tenant name lookup
  const [tenantNameMap, setTenantNameMap] = useState({});
  useEffect(() => {
    fetchTenants().then((list) => {
      const map = {};
      for (const t of (list || [])) map[t.TenantID] = t.FriendlyName || t.Domain || t.TenantID;
      setTenantNameMap(map);
    }).catch(() => {});
  }, []);

  // Resizable columns: [chevron, eventTime, tenant, user, clientIp, operation, logType, appDisplayName, matchReason]
  const [colWidths, setColWidths] = useState([36, 160, 140, 200, 130, 180, 100, 150, 220]);
  const thRefs = useRef([]);
  const tableRef = useRef(null);

  const startResize = useCallback((e, colIdx) => {
    e.preventDefault();
    const th = thRefs.current[colIdx];
    const table = tableRef.current;
    if (!th) return;
    const startX = e.clientX;
    const startWidth = th.offsetWidth;
    const startTableWidth = table ? table.offsetWidth : 0;
    let lastWidth = startWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMouseMove = (moveEvt) => {
      lastWidth = Math.max(50, startWidth + moveEvt.clientX - startX);
      th.style.width = `${lastWidth}px`;
      if (table) table.style.width = `${startTableWidth + (lastWidth - startWidth)}px`;
    };
    const onMouseUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      setColWidths(prev => { const n = [...prev]; n[colIdx] = lastWidth; return n; });
      if (table) table.style.width = '';
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, []);

  // Table state
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('eventTime');
  const [sortDir, setSortDir] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [expandedRow, setExpandedRow] = useState(null);
  // Baseline "IP in baseline" → click the count to open the full baseline-IP list
  // drawer; value is { eventIp, windows } for the clicked row (null = closed).
  const [ipDrawer, setIpDrawer] = useState(null);
  const [showNotAppliedRows, setShowNotAppliedRows] = useState(new Set());

  // On-demand IP enrichment refresh — button in the Baseline Comparison header
  // of the expanded row (parity with AlertsPage), available to every portal user.
  // enrichOverrideByKey is keyed by rowKey so a refresh sticks to the row it
  // was triggered from, even if the user collapses + re-expands or jumps
  // between rows; entries survive only for the lifetime of the page mount.
  const [enrichOverrideByKey, setEnrichOverrideByKey] = useState({});
  const [refreshingIpForRow, setRefreshingIpForRow] = useState(null); // string `${rowKey}|${ip}` while in-flight
  const [refreshErrorByKey, setRefreshErrorByKey] = useState({});

  const handleRefreshIpEnrichmentRow = useCallback(async (rowKey, ip, force = false) => {
    if (!ip || !rowKey) return;
    const inFlightKey = `${rowKey}|${ip}`;
    try {
      setRefreshingIpForRow(inFlightKey);
      setRefreshErrorByKey(prev => { const next = { ...prev }; delete next[rowKey]; return next; });
      const fresh = await refreshIpEnrichment(ip, { force });
      setEnrichOverrideByKey(prev => ({
        ...prev,
        [rowKey]: {
          ipRiskLevel:  fresh.ipRiskLevel ?? null,
          ipCountry:    fresh.ipCountry ?? null,
          ipProvider:   fresh.ipProvider ?? null,
          ipDetections: fresh.ipDetections ?? null,
          regionName:   fresh.regionName ?? null,
          city:         fresh.city ?? null,
        },
      }));
    } catch (e) {
      if (e?.status === 409 && !force) {
        const lastUpdated = e.body?.lastUpdated;
        const hours = lastUpdated
          ? Math.max(0, Math.round((Date.now() - new Date(lastUpdated).getTime()) / 3600000))
          : null;
        const msg = hours != null
          ? `IP enrichment was last refreshed ${hours}h ago. Re-run anyway?`
          : 'IP enrichment was refreshed in the last 24h. Re-run anyway?';
        if (window.confirm(msg)) {
          return handleRefreshIpEnrichmentRow(rowKey, ip, true);
        }
        return;
      }
      const errMsg = e?.message || 'Refresh failed.';
      setRefreshErrorByKey(prev => ({ ...prev, [rowKey]: errMsg }));
    } finally {
      setRefreshingIpForRow(null);
    }
  }, []);

  // Timezone — shared TZ_STORAGE_KEY with AlertsPage / CaseDetail
  const [selectedTz, setSelectedTz] = useState(getDefaultTimezone);
  const tzObj = TIMEZONE_OPTIONS.find(t => t.id === selectedTz) || TIMEZONE_OPTIONS[0];
  const handleTzChange = (tz) => {
    setSelectedTz(tz);
    try { localStorage.setItem(TZ_STORAGE_KEY, tz); } catch { /* ignore */ }
  };

  // ── Polling logic ───────────────────────────────────────────────────
  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    stopPolling();
    setPhase('running');
    setStatusMessage('Test run in progress...');
    setErrorMessage(null);
    runStartRef.current = runStartRef.current || Date.now();

    pollTimerRef.current = setInterval(async () => {
      try {
        const status = await pollTestRunStatus(ruleId, jobId);

        // Seed elapsed timer from job startTime if not already seeded
        startElapsedTimer(status.startTime);
        if (status.startTime) setRunStartTime(status.startTime);
        if (status.endTime) setRunEndTime(status.endTime);

        // Pick up estimate on first poll that returns it (API includes it on every Running poll)
        // Add 60s buffer — actual runs consistently take longer than the stored duration
        if (status.estimateSeconds != null) setEstimateSeconds(status.estimateSeconds + 60);
        if (status.warehouseRunning != null) setWarehouseRunning(status.warehouseRunning);

        setStatusMessage(status.message || `Status: ${status.status}`);

        if (status.status === 'Completed') {
          stopPolling();
          stopElapsedTimer();
          setMatchCount(status.matchCount);
          // Fetch results — use a large hours window to capture results from this run
          try {
            const data = await fetchTestResults({ ruleId, hours: 0 });
            setResults(data || []);
          } catch {
            setResults([]);
          }
          setPhase('completed');
        } else if (status.status === 'Errored') {
          stopPolling();
          stopElapsedTimer();
          setErrorMessage(status.sparkError || status.message || 'Detection rule failed to execute.');
          setPhase('spark_error');
        } else if (['Failed', 'Stopped', 'Suspended'].includes(status.status)) {
          stopPolling();
          stopElapsedTimer();
          setErrorMessage(status.message || 'Test run failed.');
          setPhase('error');
        }
      } catch (err) {
        stopPolling();
        stopElapsedTimer();
        setErrorMessage(err.message || 'Failed to poll test run status.');
        setPhase('error');
      }
    }, POLL_INTERVAL_MS);
  }, [ruleId, jobId, stopPolling]);

  // Load results directly (viewing a previously-completed run)
  const loadResultsDirectly = useCallback(async () => {
    setPhase('running');
    setStatusMessage('Loading test results...');
    try {
      const data = await fetchTestResults({ ruleId, hours: 0 });
      setResults(data || []);
      setMatchCount((data || []).length);
      setPhase('completed');
    } catch (err) {
      setErrorMessage(err.message || 'Failed to load test results.');
      setPhase('error');
    }
  }, [ruleId]);

  // On mount: do one status poll — if already completed load results directly, otherwise start polling
  useEffect(() => {
    pollTestRunStatus(ruleId, jobId)
      .then((status) => {
        if (status.startTime) setRunStartTime(status.startTime);
        if (status.endTime) setRunEndTime(status.endTime);
        if (status.status === 'Completed') {
          setMatchCount(status.matchCount);
          loadResultsDirectly();
        } else if (status.status === 'Errored') {
          setErrorMessage(status.sparkError || status.message || 'Detection rule failed to execute.');
          setPhase('spark_error');
        } else if (['Failed', 'Stopped', 'Suspended'].includes(status.status)) {
          setErrorMessage(status.message || 'Test run failed.');
          setPhase('error');
        } else {
          startPolling();
        }
      })
      .catch(() => {
        // Job not found (old run) — load whatever results exist for the rule
        loadResultsDirectly();
      });
    return () => { stopPolling(); stopElapsedTimer(); };
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // ── Filtered / sorted / paginated results ───────────────────────────
  const filtered = results.filter((r) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (r.userId || '').toLowerCase().includes(term) ||
      (r.clientIp || '').toLowerCase().includes(term) ||
      (r.operation || '').toLowerCase().includes(term) ||
      (r.tenantId || '').toLowerCase().includes(term) ||
      (tenantNameMap[r.tenantId] || '').toLowerCase().includes(term) ||
      (r.logType || '').toLowerCase().includes(term) ||
      (r.appDisplayName || '').toLowerCase().includes(term)
    );
  });

  const sorted = [...filtered].sort((a, b) => {
    const aVal = a[sortField] || '';
    const bVal = b[sortField] || '';
    const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paged = sorted.slice((safePage - 1) * pageSize, safePage * pageSize);

  // Directory object GUID -> friendly-name resolution for the expanded row's
  // Target Resource panel (directory_audit / CA policy edits — modifiedProperties
  // reference groups / users / directory roles by bare GUID). Keyed by rowKey so
  // the resolved names stick to their row. Display-only. (rule #13 parity with
  // AlertsPage's directory-name resolution.)
  const [dirNamesByRow, setDirNamesByRow] = useState({});
  useEffect(() => {
    if (!expandedRow || dirNamesByRow[expandedRow]) return;
    const row = paged.find((r, i) => (r.alertId || r.testResultId || `${r.eventId}-${i}`) === expandedRow);
    if (!row) return;
    const tenantId = row.tenantId || row.tenant_id;
    if (!tenantId) return;
    let rawEvent = null;
    try { rawEvent = row.rawEventJson ? JSON.parse(row.rawEventJson) : null; } catch { rawEvent = null; }
    const ids = collectTargetResourceGuids(rawEvent);
    if (ids.length === 0) return;
    let cancelled = false;
    resolveDirectoryNames(tenantId, ids)
      .then(d => { if (!cancelled && d?.resolved) setDirNamesByRow(prev => ({ ...prev, [expandedRow]: d.resolved })); })
      .catch(() => { /* display-only; ignore */ });
    return () => { cancelled = true; };
  }, [expandedRow, paged, dirNamesByRow]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setCurrentPage(1);
  };

  const sortIcon = (field) => {
    if (sortField !== field) return '';
    return sortDir === 'asc' ? ' \u25B2' : ' \u25BC';
  };

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <div className="p-6 max-w-full">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <button
          onClick={() => navigate('/admin/alert-rules')}
          className="text-gray-400 hover:text-white transition-colors"
          title="Back to Alert Rules"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-xl font-semibold text-white">Test Run Results</h1>
          <p className="text-sm text-gray-400">
            {ruleName}
            {dateRange && <span className="ml-2 text-gray-500">| {dateRange}</span>}
            <span className="ml-2 text-gray-500">| {tenantLabel}</span>
            {runEndTime && (
              <span className="ml-2 text-gray-500" title={`Run started: ${formatDateTime(runStartTime, tzObj.iana)}`}>
                | Run completed: {formatDateTime(runEndTime, tzObj.iana)}
              </span>
            )}
            {!runEndTime && runStartTime && (
              <span className="ml-2 text-gray-500">| Run started: {formatDateTime(runStartTime, tzObj.iana)}</span>
            )}
          </p>
        </div>
      </div>

      {/* Running phase */}
      {phase === 'running' && (
        <div className="card p-8 flex flex-col items-center justify-center gap-3">
          <svg className="animate-spin h-8 w-8 text-blue-400" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <p className="text-gray-300">
            {statusMessage}
            {elapsedSeconds > 0 && (
              <span className="ml-2 text-gray-500 text-sm">({formatElapsed(elapsedSeconds)} elapsed)</span>
            )}
          </p>
          {estimateSeconds != null && (() => {
            const remaining = estimateSeconds - elapsedSeconds;
            const overrun = remaining < 0;
            const warmupNote = warehouseRunning === false ? ' (includes ~6m cluster warm-up)' : '';
            return overrun ? (
              <p className="text-xs text-amber-400">Taking longer than estimated — still running</p>
            ) : (
              <p className="text-xs text-gray-500">
                Est. ~{formatElapsed(remaining)} remaining{warmupNote}
              </p>
            );
          })()}
        </div>
      )}

      {/* Error phase */}
      {phase === 'error' && (
        <div className="card p-6">
          <div className="flex items-start gap-3 text-red-400 mb-4">
            <svg className="h-5 w-5 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
            </svg>
            <div>
              <p className="font-medium">Test run failed</p>
              <p className="text-sm text-red-300 mt-1">{errorMessage}</p>
            </div>
          </div>
          <button onClick={() => navigate('/admin/alert-rules')} className="btn-secondary text-sm">
            Back to Alert Rules
          </button>
        </div>
      )}

      {/* Spark rule error phase — distinct from infra failure */}
      {phase === 'spark_error' && (
        <div className="card p-6">
          <div className="flex items-start gap-3 mb-4">
            <ExclamationTriangleIcon className="h-5 w-5 mt-0.5 flex-shrink-0 text-orange-400" />
            <div className="flex-1">
              <p className="font-medium text-orange-300">Rule error — can't execute this detection</p>
              <p className="text-sm text-orange-200/80 mt-1">
                The Databricks engine raised an error while running this rule. This usually means a
                match-condition field name doesn't exist in the event, or a JSON path couldn't resolve.
              </p>
              <pre className="mt-3 text-xs font-mono bg-gray-950 border border-orange-800/40 rounded p-3 text-orange-200 whitespace-pre-wrap break-all max-h-64 overflow-y-auto">
                {errorMessage}
              </pre>
              <p className="text-xs text-gray-500 mt-3">
                Tip: top-level columns are case-insensitive (e.g. <code className="text-gray-300">UserId</code>).
                JSON paths must be case-sensitive and prefixed with <code className="text-gray-300">$.</code> —
                Graph API uses camelCase (<code className="text-gray-300">$.riskLevel</code>),
                O365 uses PascalCase (<code className="text-gray-300">$.ClientInfoString</code>).
              </p>
            </div>
          </div>
          <button onClick={() => navigate('/admin/alert-rules')} className="btn-secondary text-sm">
            Back to Alert Rules
          </button>
        </div>
      )}

      {/* Completed phase */}
      {phase === 'completed' && (
        <>
          {/* Summary bar */}
          <div className="card p-3 mb-4 flex items-center justify-between">
            <div className="flex items-center gap-4 text-sm">
              <span className="text-white font-medium">
                {matchCount !== null ? matchCount : results.length} matching event{(matchCount ?? results.length) !== 1 ? 's' : ''}
              </span>
              {dateRange && <span className="text-gray-400">{dateRange}</span>}
              <span className="text-gray-500">{tenantLabel}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <MagnifyingGlassIcon className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  placeholder="Filter results..."
                  className="input-field text-sm pl-8 w-56"
                />
              </div>
              {/* Timezone picker */}
              <select
                value={selectedTz}
                onChange={(e) => handleTzChange(e.target.value)}
                className="bg-gray-800 border border-gray-700 text-gray-300 text-sm rounded px-2 py-1.5 focus:ring-blue-500 focus:border-blue-500"
              >
                {TIMEZONE_OPTIONS.map(tz => (
                  <option key={tz.id} value={tz.id}>{tz.label} ({getTzOffset(tz.iana)})</option>
                ))}
              </select>
            </div>
          </div>

          {/* Results table */}
          {sorted.length === 0 ? (
            <div className="card p-8 text-center text-gray-400">
              {results.length === 0
                ? 'No matching events found for the specified date range and rule.'
                : 'No results match the current filter.'}
            </div>
          ) : (
            <div className="card overflow-x-auto">
              <table
                ref={tableRef}
                className="text-sm"
                style={{ tableLayout: 'fixed', width: `${colWidths.reduce((a, b) => a + b, 0)}px` }}
              >
                <thead>
                  <tr className="border-b border-gray-700 text-left">
                    {/* Chevron column */}
                    <th
                      ref={(el) => { thRefs.current[0] = el; }}
                      className="p-2 relative select-none"
                      style={{ width: `${colWidths[0]}px` }}
                    >
                      <div
                        className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-10 flex items-stretch justify-center group"
                        onMouseDown={(e) => startResize(e, 0)}
                      >
                        <div className="w-px bg-gray-700/50 group-hover:bg-blue-500/70 transition-colors" />
                      </div>
                    </th>
                    {[
                      { key: 'eventTime', label: 'Event Time' },
                      { key: 'tenantId', label: 'Tenant' },
                      { key: 'userId', label: 'User' },
                      { key: 'clientIp', label: 'Client IP' },
                      { key: 'operation', label: 'Operation' },
                      { key: 'logType', label: 'Log Type' },
                      { key: 'appDisplayName', label: 'Application' },
                      { key: 'matchReason', label: 'Match Reason', noSort: true },
                    ].map((col, i) => (
                      <th
                        key={col.key}
                        ref={(el) => { thRefs.current[i + 1] = el; }}
                        className={`p-2 text-gray-400 font-medium select-none whitespace-nowrap relative ${col.noSort ? '' : 'cursor-pointer hover:text-white'}`}
                        style={{ width: `${colWidths[i + 1]}px` }}
                        onClick={() => !col.noSort && handleSort(col.key)}
                      >
                        <span className="truncate">{col.label}{!col.noSort && sortIcon(col.key)}</span>
                        <div
                          className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-10 flex items-stretch justify-center group"
                          onMouseDown={(e) => { e.stopPropagation(); startResize(e, i + 1); }}
                        >
                          <div className="w-px bg-gray-700/50 group-hover:bg-blue-500/70 transition-colors" />
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paged.map((row, idx) => {
                    const rowKey = row.alertId || row.testResultId || `${row.eventId}-${idx}`;
                    const isExpanded = expandedRow === rowKey;
                    return (
                      <React.Fragment key={rowKey}>
                        <tr
                          className="border-b border-gray-700/50 hover:bg-gray-700/30 cursor-pointer"
                          onClick={() => setExpandedRow(isExpanded ? null : rowKey)}
                        >
                          <td className="p-2 text-gray-500">
                            {isExpanded
                              ? <ChevronDownIcon className="h-3.5 w-3.5" />
                              : <ChevronRightIcon className="h-3.5 w-3.5" />}
                          </td>
                          <td className="p-2 text-gray-300 overflow-hidden truncate whitespace-nowrap">{formatDateTime(row.eventTime, tzObj.iana)}</td>
                          <td className="p-2 text-gray-300 overflow-hidden truncate" title={tenantNameMap[row.tenantId] || row.tenantId}>{tenantNameMap[row.tenantId] || row.tenantId}</td>
                          <td className="p-2 text-gray-300 overflow-hidden truncate" title={alertFactsUserCell(row.alertFactsJson)?.title || row.userId}>{displayActor(row)}</td>
                          <td className="p-2 text-gray-300 font-mono text-xs overflow-hidden truncate">{row.clientIp}</td>
                          <td className="p-2 text-gray-300 overflow-hidden truncate" title={row.operation}>{row.operation}</td>
                          <td className="p-2 text-gray-400 overflow-hidden truncate">{row.logType}</td>
                          <td className="p-2 text-gray-400 overflow-hidden truncate" title={row.appDisplayName}>{row.appDisplayName}</td>
                          <td className="p-2 overflow-hidden">{renderMatchReason(row)}</td>
                        </tr>
                        {isExpanded && (
                          <tr className="bg-gray-900/50 border-b-2 border-gray-700">
                            <td colSpan={9} className="pl-10 pr-3 py-3 space-y-3 border-l-4 border-blue-600/40">
                              {/* ── Sign-in outcome (Defender/AADIP pass-through) — the
                                    "PASSWORD COMPROMISED" callout, read-only on test rows
                                    (no re-check button — test results have no alert to
                                    persist to). Parity with the Alerts page (rule #13). ── */}
                              {(() => {
                                let rawEvtSo = null;
                                try { rawEvtSo = row.rawEventJson && !row.rawEventJson.startsWith('blob:') ? JSON.parse(row.rawEventJson) : null; } catch { /* ignore */ }
                                return (
                                  <SigninOutcomeBanner
                                    alert={row}
                                    rawEvent={rawEvtSo}
                                    tzIana={tzObj.iana}
                                    showRecheckButton={false}
                                    boxed
                                  />
                                );
                              })()}
                              {/* ── Source audit event link (Defender pass-through): read-only
                                    on test rows — no button, no write-back (test results have no
                                    alert row). Renders the pipeline's inline `_linked_audit_event`
                                    when present. Parity with the Alerts page (rule #13). ── */}
                              {(() => {
                                let rawEvtLa = null;
                                try { rawEvtLa = row.rawEventJson && !row.rawEventJson.startsWith('blob:') ? JSON.parse(row.rawEventJson) : null; } catch { /* ignore */ }
                                return (
                                  <LinkedAuditEventPanel
                                    alert={row}
                                    rawEvent={rawEvtLa}
                                    tzIana={tzObj.iana}
                                    showLinkButton={false}
                                    boxed
                                  />
                                );
                              })()}
                              {/* ── Defender threat highlight (security_alert evidence headline) ── */}
                              {(() => {
                                if (row.logType !== 'security_alert') return null;
                                let rawEvtSa = null;
                                try { rawEvtSa = row.rawEventJson && !row.rawEventJson.startsWith('blob:') ? JSON.parse(row.rawEventJson) : null; } catch { /* ignore */ }
                                if (!rawEvtSa) return null;
                                const hl = topEvidenceHighlight(parseSecurityAlert(rawEvtSa));
                                if (!hl) return null;
                                return (
                                  <div className={`rounded border p-3 ${
                                    hl.tone === 'critical' ? 'border-red-700/50 bg-red-950/20'
                                      : hl.tone === 'warning' ? 'border-amber-700/50 bg-amber-950/20'
                                      : 'border-gray-700/50 bg-gray-900/40'
                                  }`}>
                                    <p className={`text-[10px] uppercase tracking-wide font-semibold mb-1 ${
                                      hl.tone === 'critical' ? 'text-red-400'
                                        : hl.tone === 'warning' ? 'text-amber-400'
                                        : 'text-gray-400'
                                    }`}>
                                      Threat Evidence{hl.verdict && hl.verdict !== 'unknown' ? ` · ${hl.verdict}` : ''}
                                    </p>
                                    <p className="text-gray-100 break-words font-medium text-xs">
                                      {hl.headline}
                                      {hl.moreCount > 0 && (
                                        <span className="text-gray-500 font-normal"> · +{hl.moreCount} more</span>
                                      )}
                                    </p>
                                  </div>
                                );
                              })()}
                              {/* ── AiTM Click Correlation (rule_aitm_url_click_to_new_ip_signin) ── */}
                              {(() => {
                                let rawEvtAitm = null;
                                try { rawEvtAitm = row.rawEventJson && !row.rawEventJson.startsWith('blob:') ? JSON.parse(row.rawEventJson) : null; } catch { /* ignore */ }
                                const aitm = rawEvtAitm?._aitm_correlation;
                                if (!aitm) return null;
                                return (
                                  <div className="rounded border border-rose-700/40 bg-rose-950/20 p-3">
                                    <p className="text-rose-400 text-[10px] uppercase tracking-wide font-semibold mb-2">AiTM Click Correlation</p>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-2 text-xs">
                                      <div className="md:col-span-3">
                                        <p className="text-gray-500 text-[10px] uppercase tracking-wide mb-0.5">Clicked URL</p>
                                        {aitm.clicked_url ? (
                                          <span className="group inline-flex items-center gap-1 max-w-full">
                                            {/* Defanged display: dot between registrable domain and TLD becomes [.] so the URL doesn't auto-link */}
                                            <span className="font-mono text-rose-300 break-all" title={aitm.clicked_url}>
                                              {defangUrl(aitm.clicked_url)}
                                            </span>
                                            <CopyButton text={aitm.clicked_url} />
                                          </span>
                                        ) : (<span className="text-gray-600 italic">—</span>)}
                                      </div>
                                      <div>
                                        <p className="text-gray-500 text-[10px] uppercase tracking-wide mb-0.5">Click Time</p>
                                        <p className="text-gray-300 font-mono">{aitm.click_time ? formatDateTime(aitm.click_time, tzObj.iana) : '—'}</p>
                                      </div>
                                      <div>
                                        <p className="text-gray-500 text-[10px] uppercase tracking-wide mb-0.5">Click → Sign-in Gap</p>
                                        <p className="text-gray-300 font-mono">
                                          {(typeof aitm.click_to_signin_seconds === 'number')
                                            ? (aitm.click_to_signin_seconds < 60
                                                ? `${aitm.click_to_signin_seconds} seconds`
                                                : `${Math.floor(aitm.click_to_signin_seconds / 60)} minutes ${aitm.click_to_signin_seconds % 60} seconds`)
                                            : '—'}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()}
                              {/* ── Aggregation Summary — surfaces detection-window facts
                                    (mailbox owner, window, folders, IPs, client info) when
                                    raw_event_json is a detection-window payload. ── */}
                              <AggregationSummary rawEventJson={row.rawEventJson} tzIana={tzObj.iana} />
                              {/* ── Aggregation Source Events (blob) ── */}
                              {row.sourceEventsBlobPath?.startsWith('blob:') && (
                                <SourceEventsPanel blobPath={row.sourceEventsBlobPath} tzIana={tzObj.iana} rawEventJson={row.rawEventJson} tenantId={row.tenantId || row.tenant_id} alertId={row.alertId || row.rowKey} ruleName={row.ruleName || row.rule_name} source="test-run" />
                              )}
                              {/* ── Alert facts (pipeline-defined per-kind detail; replaces the
                                    parameters table below). Rendered OUTSIDE the raw-event
                                    block so it still shows when raw_event_json spilled to a
                                    blob — the facts are a separate, small property. ── */}
                              <AlertFactsPanel alertFactsJson={row.alertFactsJson} />
                              {/* ── Source Event header (suppressed when blob events or aggregation summary present) ── */}
                              {(() => {
                                if (row.sourceEventsBlobPath?.startsWith('blob:')) return null;
                                if (parseAggregationFields(row.rawEventJson)) return null;
                                let rawEvt = null;
                                try { rawEvt = row.rawEventJson && !row.rawEventJson.startsWith('blob:') ? JSON.parse(row.rawEventJson) : null; } catch { /* ignore */ }
                                if (!rawEvt) return null;
                                // Inbox-rule / operation parameters — covers the flat `Parameters`
                                // schema (New-/Set-/Remove-InboxRule) AND `OperationProperties`
                                // (UpdateInboxRules; forward-to target in RuleActions). See
                                // exchangeRuleHelpers.
                                // Suppressed when the row carries pipeline facts (AlertFactsPanel above).
                                const params = parseAlertFacts(row.alertFactsJson) ? null : parseInboxRuleParameters(rawEvt);

                                return (
                                  <>
                                    <CollapsibleSection title="Source Event (Microsoft data)">
                                      <SourceEventRenderer
                                        rawEvent={rawEvt}
                                        logType={row.logType}
                                        tzIana={tzObj.iana}
                                        fallbacks={{
                                          user: row.userId,
                                          ip: row.clientIp,
                                          app: row.appDisplayName,
                                          operation: row.operation,
                                          device: row.deviceName,
                                        }}
                                      />
                                    </CollapsibleSection>
                                    {/* ── Parameters table (Exchange inbox rules, Set- ops) ── */}
                                    {params && (
                                      <div>
                                        <div className="text-xs text-gray-400 mb-2 font-medium uppercase tracking-wide">Rule / Operation Parameters</div>
                                        <table className="w-full max-w-2xl text-xs">
                                          <tbody>
                                            {params.map(({ Name, Value }) => (
                                              <tr key={Name} className="border-b border-gray-800/40 last:border-0">
                                                <td className="py-1 pr-6 text-gray-400 w-56">{Name}</td>
                                                <td className="py-1 text-gray-300 break-all">{Value}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                  </>
                                );
                              })()}

                              {/* IP Intelligence + Sign-in Device side-by-side */}
                              {(() => {
                                let dd = null;
                                let rawEvtIp = null;
                                if (row.rawEventJson && !row.rawEventJson.startsWith('blob:')) {
                                  try {
                                    const re = JSON.parse(row.rawEventJson);
                                    dd = re.deviceDetail || null;
                                    rawEvtIp = re;
                                  } catch { /* ignore */ }
                                }
                                // Defender security_alert pass-through rows keep the IP +
                                // country inside evidence[] (ipEvidence); the row's client_ip /
                                // ip_country are blank. Lift them out so the panel renders.
                                const saIp = (row.logType === 'security_alert' && rawEvtIp)
                                  ? securityAlertIpEvidence(parseSecurityAlert(rawEvtIp)) : null;
                                const effectiveIp = (row.clientIp ? stripPort(row.clientIp) : null) || saIp?.ip || null;
                                // Overlay any on-demand IP enrichment refresh
                                // for this row so this block re-renders alongside
                                // Baseline Comparison.
                                const ipOverride    = enrichOverrideByKey[rowKey] || null;
                                const viewRiskLevel  = ipOverride?.ipRiskLevel  ?? row.ipRiskLevel;
                                const viewCountry    = ipOverride?.ipCountry    ?? row.ipCountry ?? saIp?.country;
                                const viewProvider   = ipOverride?.ipProvider   ?? row.ipProvider;
                                const viewDetections = ipOverride?.ipDetections ?? row.ipDetections;
                                // ProxyCheck-only `provider` is the reliable "was enriched"
                                // signal; security_alert rows ship ip_risk_level=0 by default,
                                // so a green "0" misreads as low-risk until enriched → show "—".
                                const hasRealEnrichment = !!(ipOverride || row.ipProvider);
                                const displayRiskLevel = (row.logType === 'security_alert' && !hasRealEnrichment)
                                  ? null : viewRiskLevel;

                                const hasIpIntel = effectiveIp || viewRiskLevel != null || viewDetections || viewCountry || viewProvider;
                                const hasDevice  = row.deviceName || row.deviceOs || row.deviceIsCompliant || dd;
                                if (!hasIpIntel && !hasDevice) return null;

                                const complianceText = (row.deviceIsCompliant === 'true' || dd?.isCompliant === true) ? 'Yes'
                                  : (row.deviceIsCompliant === 'false' || dd?.isCompliant === false) ? 'No' : null;
                                const complianceClass = complianceText === 'Yes' ? 'text-green-400'
                                  : complianceText === 'No' ? 'text-red-400' : 'text-gray-500';

                                return (
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                                    {hasIpIntel && (
                                      <div>
                                        <CollapsibleSection title="IP Intelligence" titleClass="text-blue-400">
                                          <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                                            {effectiveIp && (
                                              <div className="col-span-2">
                                                <span className="text-gray-500">IP: </span>
                                                <a href={`https://www.iplocation.net/ip-lookup?query=${encodeURIComponent(effectiveIp)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-blue-400 hover:text-blue-300 hover:underline font-mono" onClick={(e) => e.stopPropagation()} title="Look up this IP on iplocation.net (opens a new tab)">{effectiveIp}<ArrowTopRightOnSquareIcon className="h-3 w-3 flex-none" /></a>
                                              </div>
                                            )}
                                            <div><span className="text-gray-500">Risk Score: </span><span className={`font-bold ${displayRiskLevel == null ? 'text-gray-500' : displayRiskLevel >= 100 ? 'text-red-400' : displayRiskLevel >= 30 ? 'text-amber-400' : 'text-green-400'}`}>{displayRiskLevel ?? '—'}</span></div>
                                            <div><span className="text-gray-500">Country: </span><span className="text-gray-300">{viewCountry || '—'}</span></div>
                                            <div className="col-span-2"><span className="text-gray-500">Provider: </span><span className="text-gray-300">{viewProvider || '—'}</span></div>
                                            {viewDetections && (
                                              <div className="col-span-2">
                                                <span className="text-gray-500">Detections: </span>
                                                {viewDetections.split(',').map((d, i) => (
                                                  <span key={i} className="ml-1 px-1 py-0.5 rounded text-xs bg-orange-900/40 text-orange-300 border border-orange-700/40">{d.trim()}</span>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        </CollapsibleSection>
                                      </div>
                                    )}
                                    {hasDevice && (
                                      <div>
                                        <div className="text-xs text-blue-400 mb-2 font-medium uppercase tracking-wide">Sign-in Device</div>
                                        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                                          {(row.deviceName || dd) && (
                                            <div>
                                              <span className="text-gray-500">Device: </span>
                                              {(row.deviceName || dd?.displayName) ? (
                                                <span className="text-gray-300 font-mono">{row.deviceName || dd.displayName}</span>
                                              ) : (
                                                <span className="text-gray-500 italic">Unknown</span>
                                              )}
                                            </div>
                                          )}
                                          {dd?.trustType && <div><span className="text-gray-500">Trust Type: </span><span className="text-gray-300">{dd.trustType}</span></div>}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}

                              {/* Conditional Access Policies + Rule Suppressions side-by-side */}
                              {(() => {
                                let rawEvent = null;
                                try { rawEvent = row.rawEventJson ? JSON.parse(row.rawEventJson) : null; } catch { /* ignore */ }
                                const allPolicies = rawEvent?.appliedConditionalAccessPolicies;
                                const hasPolicies = Array.isArray(allPolicies) && allPolicies.length > 0;
                                const hasSuppressions = row.skipIfEntraJoined || row.skipIfCompliantDevice || row.skipIfDrmmMatch || row.skipIfIntuneManaged || row.skipIfInTenantBaseline || row.skipIfInUserBaseline || row.skipIfCtapTrust || row.skipIpv6PrefixMatch || row.overrideIfMaliciousIp;
                                if (!hasPolicies && !hasSuppressions) return null;

                                const showNotAppliedLocal = showNotAppliedRows.has(rowKey);
                                const HIDDEN_RESULTS_LOCAL = new Set(['notApplied', 'notEnabled', 'reportOnlyNotApplied', 'reportOnlySuccess']);
                                const hiddenCountLocal = hasPolicies ? allPolicies.filter(p => HIDDEN_RESULTS_LOCAL.has(p.result)).length : 0;
                                const displayPoliciesLocal = hasPolicies ? (showNotAppliedLocal ? allPolicies : allPolicies.filter(p => !HIDDEN_RESULTS_LOCAL.has(p.result))) : [];

                                const resultBadgeClassLocal = (result) => {
                                  if (result === 'success') return 'bg-green-900/40 text-green-300 border border-green-700/40';
                                  if (result === 'failure') return 'bg-red-900/40 text-red-300 border border-red-700/40';
                                  if (result === 'reportOnlySuccess' || result === 'reportOnlyFailure') return 'bg-purple-900/40 text-purple-300 border border-purple-700/40';
                                  return 'bg-gray-800 text-gray-500 border border-gray-700';
                                };
                                const resultLabelLocal = (result) => {
                                  if (result === 'success') return 'Applied';
                                  if (result === 'failure') return 'Blocked';
                                  if (result === 'reportOnlySuccess') return 'Report (Pass)';
                                  if (result === 'reportOnlyFailure') return 'Report (Fail)';
                                  if (result === 'notApplied') return 'Not Applied';
                                  return result || '—';
                                };

                                return (
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                                    {hasPolicies && (
                                      <div>
                                        <div className="flex items-center gap-2 mb-2">
                                          <div className="text-xs text-violet-400 font-medium uppercase tracking-wide">
                                            Conditional Access Policies ({allPolicies.length})
                                          </div>
                                          {hiddenCountLocal > 0 && (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setShowNotAppliedRows(prev => {
                                                  const next = new Set(prev);
                                                  if (next.has(rowKey)) next.delete(rowKey); else next.add(rowKey);
                                                  return next;
                                                });
                                              }}
                                              className="text-[10px] px-1.5 py-0.5 rounded border border-gray-600 text-gray-400 hover:text-gray-200 hover:border-gray-400 transition-colors"
                                            >
                                              {showNotAppliedLocal ? `Hide ${hiddenCountLocal} inactive` : `Show ${hiddenCountLocal} inactive`}
                                            </button>
                                          )}
                                        </div>
                                        <div className="space-y-1">
                                          {displayPoliciesLocal.length === 0 && hiddenCountLocal > 0 ? (
                                            <div className="text-xs text-gray-500 italic">All {hiddenCountLocal} policies were inactive (not applied, not enabled, or report-only)</div>
                                          ) : (
                                            displayPoliciesLocal.map((policy, i) => (
                                              <div key={i} className="flex items-center gap-2 text-xs">
                                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${resultBadgeClassLocal(policy.result)}`}>
                                                  {resultLabelLocal(policy.result)}
                                                </span>
                                                <span className="text-gray-300">{policy.displayName || '(unnamed)'}</span>
                                                {Array.isArray(policy.enforcedGrantControls) && policy.enforcedGrantControls.length > 0 && (
                                                  <span className="text-gray-500 text-[10px]">({policy.enforcedGrantControls.join(', ')})</span>
                                                )}
                                              </div>
                                            ))
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    {hasSuppressions && (
                                      <div>
                                        <div className="text-xs text-gray-400 mb-2 font-medium uppercase tracking-wide">Rule Suppressions</div>
                                        <div className="space-y-0.5">
                                          {row.skipIfEntraJoined && (
                                            <div className="flex items-center gap-1 text-[11px] text-blue-400"><span>✓</span><span>Skip if Entra Joined</span></div>
                                          )}
                                          {row.skipIfIntuneManaged && (
                                            <div className="flex items-center gap-1 text-[11px] text-blue-400"><span>✓</span><span>Skip if Intune Managed</span></div>
                                          )}
                                          {row.skipIfCompliantDevice && (
                                            <div className="flex items-center gap-1 text-[11px] text-blue-400"><span>✓</span><span>Skip if Intune Compliant Device</span></div>
                                          )}
                                          {row.skipIfDrmmMatch && (
                                            <div className="flex items-center gap-1 text-[11px] text-blue-400"><span>✓</span><span>Skip if DRMM Match</span></div>
                                          )}
                                          {row.skipIfInTenantBaseline && (
                                            <div className="flex items-center gap-1 text-[11px] text-blue-400"><span>✓</span><span>Skip if IP in MSP Tenant Baseline</span></div>
                                          )}
                                          {row.skipIfInUserBaseline && (
                                            <div className="flex items-center gap-1 text-[11px] text-blue-400"><span>✓</span><span>Skip if IP in User Baseline</span></div>
                                          )}
                                          {row.skipIfCtapTrust && (
                                            <div className="flex items-center gap-1 text-[11px] text-blue-400"><span>✓</span><span>Skip if CTAP Inbound Trust</span></div>
                                          )}
                                          {row.skipIpv6PrefixMatch && (
                                            <div className="flex items-center gap-1 text-[11px] text-blue-400"><span>✓</span><span>IPv6 /64 prefix match (modifier)</span></div>
                                          )}
                                          {row.overrideIfMaliciousIp && (
                                            <div className="flex items-center gap-1 text-[11px] text-amber-400"><span>↺</span><span>Alert if a Known Malicious IP (override)</span></div>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}

                              {/* Authentication App Policy Evaluation (graph_signin authenticationAppPolicyEvaluationDetails) */}
                              {(() => {
                                let rawEvtAap = null;
                                try { rawEvtAap = row.rawEventJson && !row.rawEventJson.startsWith('blob:') ? JSON.parse(row.rawEventJson) : null; } catch { /* ignore */ }
                                const details = rawEvtAap?.authenticationAppPolicyEvaluationDetails;
                                if (!Array.isArray(details) || details.length === 0) return null;
                                return (
                                  <div>
                                    <div className="text-xs text-violet-400 mb-2 font-medium uppercase tracking-wide">
                                      Authentication App Policy Evaluation ({details.length})
                                    </div>
                                    <div className="space-y-1">
                                      {details.map((d, i) => {
                                        const label = humanizePolicyEval(d.policyName, d.authenticationEvaluation);
                                        const evalColor = evalTextColor(d.authenticationEvaluation);
                                        const statusDetail = humanizePolicyStatus(d.status);
                                        const showStatus = statusDetail && statusDetail !== 'unknown';
                                        return (
                                          <div key={i} className="text-xs bg-gray-950/40 rounded px-3 py-1.5">
                                            <span className={`font-medium ${evalColor}`}>{label}</span>
                                            {showStatus && <span className="text-gray-500 ml-2 text-[10px]">— {statusDetail}</span>}
                                            {d.applicationName && <span className="text-gray-600 ml-2 text-[10px]">({d.applicationName})</span>}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              })()}

                              {/* Unfamiliar Sign-in Properties — Entra ID Protection pass-through only; renders nothing
                                  when the row carries no unfamiliar_properties_json. Shared with the Alerts page (rule #13). */}
                              {row.unfamiliarPropertiesJson && (
                                <UnfamiliarPropertiesPanel json={row.unfamiliarPropertiesJson} />
                              )}

                              {/* Baseline Comparison (collapsible, full-width) — shared with the Alerts page (rule #13) */}
                              {(() => {
                                let rawEvtBl = null;
                                try { rawEvtBl = row.rawEventJson && !row.rawEventJson.startsWith('blob:') ? JSON.parse(row.rawEventJson) : null; } catch { /* ignore */ }
                                const comparison = resolveBaselineComparison({
                                  storedJson: row.baselineComparisonJson,
                                  baselineContext: row.baselineContextJson,
                                  rawEvent: rawEvtBl,
                                  correlatedSignin: row.correlatedSigninJson ?? row.correlatedSignInJson,
                                  alert: row,
                                  enrichOverride: enrichOverrideByKey[rowKey] || null,
                                });
                                const eventIp = rawEvtBl?.ipAddress ?? (row.clientIp ? stripPort(row.clientIp) : null);
                                // Sign-in based rows lead with location/device baseline signal, so expand by default for them.
                                const baselineDefaultOpen = row.logType === 'signin' || row.logType === 'graph_signin';
                                return (
                                  <BaselineComparisonPanel
                                    comparison={comparison}
                                    defaultOpen={baselineDefaultOpen}
                                    eventIp={eventIp}
                                    canRefresh={!DEMO}
                                    refreshing={refreshingIpForRow === `${rowKey}|${eventIp}`}
                                    refreshError={refreshErrorByKey[rowKey]}
                                    onRefresh={() => handleRefreshIpEnrichmentRow(rowKey, eventIp)}
                                    onOpenIpDrawer={(lists) => setIpDrawer({
                                      eventIp,
                                      windows: [
                                        { label: '7-day', ips: lists['7d'] || [] },
                                        { label: '30-day', ips: lists['30d'] || [] },
                                      ],
                                    })}
                                  />
                                );
                              })()}

                              {/* Correlated Sign-In — data-driven: shown when the pipeline writes a correlated
                                  sign-in event (operation_baseline_deviation and future rule types that correlate
                                  against sign-in activity). */}
                              {row.correlatedSigninJson && (() => {
                                let signin = null;
                                try { signin = JSON.parse(row.correlatedSigninJson); } catch { /* ignore */ }
                                // Only render if parsed object has at least one meaningful field
                                const hasData = signin && (signin.CreationTime || signin.event_time || signin.ClientIP || signin.client_ip);
                                if (!hasData) return null;
                                return (
                                  <div>
                                    <div className="text-xs text-purple-400 mb-2 font-medium uppercase tracking-wide">Correlated Sign-In</div>
                                    {signin ? (
                                      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1 text-xs">
                                        <div><span className="text-gray-500">Time: </span><span className="text-gray-300">{formatDateTime(signin.CreationTime || signin.event_time, tzObj.iana)}</span></div>
                                        <div><span className="text-gray-500">IP: </span><span className="text-gray-300 font-mono">{signin.ClientIP || signin.client_ip || '—'}</span></div>
                                        <div><span className="text-gray-500">Country: </span><span className="text-gray-300">{signin.IP_Country || signin.ip_country || '—'}</span></div>
                                        <div><span className="text-gray-500">Status: </span><span className="text-gray-300">{signin.ResultStatus || signin.result_status || '—'}</span></div>
                                        <div className="col-span-2"><span className="text-gray-500">App: </span><span className="text-gray-300">{signin.AppDisplayName || signin.app_display_name || '—'}</span></div>
                                        <div className="col-span-2"><span className="text-gray-500">OS: </span><span className="text-gray-300">{signin.OS || signin.os || '—'}</span></div>
                                      </div>
                                    ) : (
                                      <pre className="text-xs text-gray-300 bg-gray-950 rounded p-3 overflow-x-auto max-h-48 overflow-y-auto whitespace-pre-wrap break-all">
                                        {row.correlatedSigninJson}
                                      </pre>
                                    )}
                                  </div>
                                );
                              })()}

                              {/* Device Posture (Directory + MDM) — lazy-fetched on expand.
                                  Falls back to the correlated sign-in's deviceDetail.deviceId when the row's
                                  own raw event has none (Exchange / SharePoint / etc.) — the pipeline correlates
                                  every alert against the nearest graph_signin within 1h. */}
                              {(() => {
                                if (!row.tenantId) return null;
                                let rawEventDp = null;
                                try { rawEventDp = row.rawEventJson && !row.rawEventJson.startsWith('blob:') ? JSON.parse(row.rawEventJson) : null; } catch { /* ignore */ }
                                let devId = rawEventDp?.deviceDetail?.deviceId;
                                if (!devId && row.correlatedSigninJson) {
                                  try { devId = JSON.parse(row.correlatedSigninJson)?.deviceDetail?.deviceId; } catch { /* ignore */ }
                                }
                                if (!devId) return null;
                                return <DevicePosturePanel tenantId={row.tenantId} deviceId={devId} />;
                              })()}

                              {/* Source (directory audit events — who initiated the change + their IP) */}
                              {(() => {
                                let rawEvent = null;
                                try { rawEvent = row.rawEventJson ? JSON.parse(row.rawEventJson) : null; } catch { /* ignore */ }
                                const initBy = rawEvent?.initiatedBy;
                                if (!initBy) return null;
                                const u = initBy.user;
                                const a = initBy.app;
                                if (!u && !a) return null;
                                return (
                                  <div>
                                    <div className="text-xs text-cyan-400 mb-2 font-medium uppercase tracking-wide">
                                      Source
                                    </div>
                                    <div className="bg-gray-950/40 rounded p-3 text-xs">
                                      {u && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
                                          <div><span className="text-gray-500">UPN: </span><span className="text-gray-300 font-mono">{u.userPrincipalName || '—'}</span></div>
                                          <div><span className="text-gray-500">IP: </span><span className="text-gray-300 font-mono">{u.ipAddress || '—'}</span></div>
                                        </div>
                                      )}
                                      {a && (
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-1">
                                          <div><span className="text-gray-500">App Name: </span><span className="text-gray-300">{a.displayName || '—'}</span></div>
                                          <div><span className="text-gray-500">App ID: </span><span className="text-gray-300 font-mono">{a.servicePrincipalId || a.appId || '—'}</span></div>
                                          <div><span className="text-gray-500">Type: </span><span className="text-gray-300">Application</span></div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })()}

                              {/* Target Resource (directory audit events — role assignments, app consent,
                                  group membership changes, etc.) */}
                              {(() => {
                                let rawEvent = null;
                                try { rawEvent = row.rawEventJson ? JSON.parse(row.rawEventJson) : null; } catch { /* ignore */ }
                                const targets = rawEvent?.targetResources;
                                if (!Array.isArray(targets) || targets.length === 0) return null;
                                const dirNames = dirNamesByRow[rowKey] || {};
                                const meaningful = targets.filter(t =>
                                  t.displayName ||
                                  t.userPrincipalName ||
                                  (Array.isArray(t.modifiedProperties) && t.modifiedProperties.length > 0)
                                );
                                if (meaningful.length === 0) return null;
                                const fmt = (v) => {
                                  if (v === null || v === undefined) return 'null';
                                  if (v === '') return '""';
                                  if (typeof v !== 'string') return String(v);
                                  try { const parsed = JSON.parse(v); return typeof parsed === 'string' ? parsed : v; }
                                  catch { return v; }
                                };
                                // Suppress Modified Properties sub-section on consent events — those properties
                                // are redundant ConsentContext metadata, fully covered by Permissions Granted.
                                const isConsentEvent = extractConsentPermissions(rawEvent).length > 0;
                                return (
                                  <div>
                                    <div className="text-xs text-amber-400 mb-2 font-medium uppercase tracking-wide">
                                      Target Resource{meaningful.length > 1 ? 's' : ''}
                                    </div>
                                    <div className="space-y-2">
                                      {meaningful.map((t, idx) => {
                                        const wk = t.modifiedProperties?.find(p => p.displayName === 'Role.WellKnownObjectName');
                                        const wkVal = wk ? fmt(wk.newValue) : null;
                                        return (
                                          <div key={idx} className="bg-gray-950/40 rounded p-3 text-xs">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
                                              <div>
                                                <span className="text-gray-500">Display Name: </span>
                                                <span className={isConsentEvent ? 'text-gray-100 font-semibold text-sm' : 'text-gray-300'}>
                                                  {t.displayName || '—'}
                                                </span>
                                              </div>
                                              <div><span className="text-gray-500">Object ID: </span><span className="text-gray-300 font-mono">{t.id || '—'}</span></div>
                                              <div><span className="text-gray-500">UPN: </span><span className="text-gray-300 font-mono">{t.userPrincipalName || '—'}</span></div>
                                              <div><span className="text-gray-500">Type: </span><span className="text-gray-300">{t.type || '—'}</span></div>
                                            </div>
                                            {!isConsentEvent && Array.isArray(t.modifiedProperties) && t.modifiedProperties.filter(mp => !HIDDEN_MODIFIED_PROPS.has(mp.displayName)).length > 0 && (
                                              <div className="mt-2 pt-2 border-t border-gray-800">
                                                <div className="text-gray-500 mb-1">Modified Properties</div>
                                                <div className="space-y-1.5 font-mono">
                                                  {t.modifiedProperties.filter(mp => !HIDDEN_MODIFIED_PROPS.has(mp.displayName)).map((mp, mpIdx) => {
                                                    const oldParsed = tryParseAsObject(mp.oldValue);
                                                    const newParsed = tryParseAsObject(mp.newValue);
                                                    if (oldParsed && newParsed) {
                                                      const diff = computeJsonDiff(oldParsed, newParsed);
                                                      const total = diff.changed.length + diff.added.length + diff.removed.length;
                                                      if (total === 0) return (
                                                        <div key={mpIdx} className="break-all">
                                                          <span className="text-gray-400">{mp.displayName}:</span>{' '}
                                                          <span className="text-gray-600 italic">no changes detected</span>
                                                        </div>
                                                      );
                                                      return (
                                                        <div key={mpIdx}>
                                                          <div className="text-gray-400 mb-1">{mp.displayName} <span className="text-gray-600">({total} change{total !== 1 ? 's' : ''})</span></div>
                                                          <div className="ml-3 space-y-1">
                                                            {diff.changed.map((c) => (
                                                              c.type === 'array' ? (
                                                                <div key={c.path} className="space-y-0.5">
                                                                  <span className="text-gray-500">{c.path}:</span>
                                                                  {c.addedItems.map((item, i) => (
                                                                    <div key={`a${i}`} className="ml-3 flex items-start gap-2 flex-wrap">
                                                                      <span className="px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 bg-emerald-900/60 text-emerald-300 border border-emerald-700/60">Added</span>
                                                                      <ResolvedId value={item} names={dirNames} valueClass="text-emerald-400 font-mono" />
                                                                    </div>
                                                                  ))}
                                                                  {c.removedItems.map((item, i) => (
                                                                    <div key={`r${i}`} className="ml-3 flex items-start gap-2 flex-wrap">
                                                                      <span className="px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 bg-red-900/60 text-red-300 border border-red-700/60">Removed</span>
                                                                      <ResolvedId value={item} names={dirNames} valueClass="text-red-400/70 font-mono" />
                                                                    </div>
                                                                  ))}
                                                                </div>
                                                              ) : (
                                                                <div key={c.path} className="flex items-start gap-2 flex-wrap break-all">
                                                                  <span className="px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 bg-amber-900/60 text-amber-300 border border-amber-700/60">Changed</span>
                                                                  <span className="text-gray-500">{c.path}:</span>
                                                                  <ResolvedId value={c.oldVal} names={dirNames} valueClass="text-red-400/70" />
                                                                  <span className="text-gray-600">→</span>
                                                                  <ResolvedId value={c.newVal} names={dirNames} valueClass="text-emerald-400" />
                                                                </div>
                                                              )
                                                            ))}
                                                            {diff.added.map((a) => (
                                                              <div key={a.path} className="flex items-start gap-2 flex-wrap break-all">
                                                                <span className="px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 bg-emerald-900/60 text-emerald-300 border border-emerald-700/60">Added</span>
                                                                <span className="text-gray-400">{a.path}:</span>
                                                                <ResolvedId value={a.value} names={dirNames} valueClass="text-emerald-400 font-mono" />
                                                              </div>
                                                            ))}
                                                            {diff.removed.map((r) => (
                                                              <div key={r.path} className="flex items-start gap-2 flex-wrap break-all">
                                                                <span className="px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 bg-red-900/60 text-red-300 border border-red-700/60">Removed</span>
                                                                <span className="text-gray-400">{r.path}:</span>
                                                                <ResolvedId value={r.value} names={dirNames} valueClass="text-red-400/70 font-mono" />
                                                              </div>
                                                            ))}
                                                          </div>
                                                        </div>
                                                      );
                                                    }

                                                    // One-sided object change: a whole object was created
                                                    // (old empty → new object) or deleted (old object → new empty).
                                                    // The classic case is a CA policy SoftDelete, where newValue
                                                    // is the literal "null". Render the object's settings as a
                                                    // structured, color-coded key→value list instead of dumping
                                                    // the raw JSON string inline.
                                                    const createdSide = newParsed && isNullishVal(mp.oldValue);
                                                    const deletedSide = oldParsed && isNullishVal(mp.newValue);
                                                    if (createdSide || deletedSide) {
                                                      const obj = createdSide ? newParsed : oldParsed;
                                                      const flat = flattenObj(obj);
                                                      const entries = Object.entries(flat)
                                                        .filter(([p]) => !DIFF_SKIP_ROOTS.has(p.split('.')[0]));
                                                      const valClass = createdSide ? 'text-emerald-400' : 'text-red-400/70';
                                                      return (
                                                        <div key={mpIdx}>
                                                          <div className="text-gray-400 mb-1 flex items-center gap-2 flex-wrap">
                                                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${createdSide
                                                              ? 'bg-emerald-900/60 text-emerald-300 border border-emerald-700/60'
                                                              : 'bg-red-900/60 text-red-300 border border-red-700/60'}`}>
                                                              {createdSide ? 'Created' : 'Deleted'}
                                                            </span>
                                                            <span>{mp.displayName}</span>
                                                            <span className="text-gray-600">({entries.length} setting{entries.length !== 1 ? 's' : ''})</span>
                                                          </div>
                                                          <div className="ml-3 space-y-0.5">
                                                            {entries.map(([path, value]) => (
                                                              <div key={path} className="flex items-start gap-2 flex-wrap break-all">
                                                                <span className="text-gray-500">{path}:</span>
                                                                <ResolvedId value={value} names={dirNames} valueClass={`${valClass} font-mono`} />
                                                              </div>
                                                            ))}
                                                          </div>
                                                        </div>
                                                      );
                                                    }

                                                    const oldV = fmt(mp.oldValue);
                                                    let newV = fmt(mp.newValue);
                                                    if (mp.displayName === 'Role.DisplayName' && wkVal && wkVal !== 'null') {
                                                      newV = `${newV} (${wkVal})`;
                                                    }
                                                    return (
                                                      <div key={mpIdx} className="break-all">
                                                        <span className="text-gray-400">{mp.displayName}:</span>{' '}
                                                        <span className="text-gray-500">From</span>{' '}
                                                        <span className="text-gray-300">"{oldV}"</span>{' '}
                                                        <span className="text-gray-500">to</span>{' '}
                                                        <span className="text-gray-300">"{newV}"</span>
                                                      </div>
                                                    );
                                                  })}
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              })()}

                              {/* Permissions Granted (consent-to-app — handles both correlated-events
                                  shape and ConsentAction.Permissions blob shape; see
                                  extractConsentPermissions) */}
                              {(() => {
                                let rawEvent = null;
                                try { rawEvent = row.rawEventJson ? JSON.parse(row.rawEventJson) : null; } catch { /* ignore */ }
                                const permissions = extractConsentPermissions(rawEvent);
                                if (permissions.length === 0) return null;
                                return (
                                  <div>
                                    <div className="text-xs text-violet-400 mb-2 font-medium uppercase tracking-wide">
                                      Permissions Granted
                                    </div>
                                    <div className="bg-gray-950/40 rounded p-3 text-xs space-y-1">
                                      {permissions.map((p, i) => {
                                        const statusClass = p.status === 'new'
                                          ? 'bg-emerald-900/60 text-emerald-300 border border-emerald-700/60'
                                          : p.status === 'revoked'
                                          ? 'bg-red-900/60 text-red-300 border border-red-700/60'
                                          : 'bg-gray-800/80 text-gray-400 border border-gray-700/60';
                                        const statusLabel = p.status === 'new' ? 'New' : p.status === 'revoked' ? 'Revoked' : 'Existing';
                                        return (
                                          <div key={i} className="flex items-start gap-2 flex-wrap">
                                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${statusClass}`}>
                                              {statusLabel}
                                            </span>
                                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${
                                              p.kind === 'Application'
                                                ? 'bg-rose-900/50 text-rose-300 border border-rose-700/60'
                                                : 'bg-sky-900/50 text-sky-300 border border-sky-700/60'
                                            }`}>
                                              {p.kind}
                                            </span>
                                            {p.api && <span className="text-gray-500">{p.api}:</span>}
                                            <span className={`font-mono font-semibold ${p.status === 'revoked' ? 'text-gray-500 line-through' : 'text-gray-200'}`}>{p.scope}</span>
                                            {p.description && <span className="text-gray-400">— {p.description}</span>}
                                            {p.consentType && <span className="text-gray-500 text-[10px]">({p.consentType})</span>}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              })()}

                              {/* Raw Event JSON */}
                              <RawEventJsonPanel
                                title="Raw Event JSON"
                                rawEventJson={row.rawEventJson || ''}
                              />

                              {/* IP Enrichment JSON */}
                              {row.ipEnrichmentJson && (
                                <RawEventJsonPanel
                                  title={`IP Enrichment Record — ${row.clientIp}`}
                                  rawEventJson={row.ipEnrichmentJson}
                                />
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>

              {/* Pagination */}
              <div className="p-2 border-t border-gray-700">
                <Pagination
                  currentPage={safePage}
                  totalPages={totalPages}
                  pageSize={pageSize}
                  totalItems={sorted.length}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
                />
              </div>
            </div>
          )}
        </>
      )}
      {ipDrawer && (
        <BaselineIpListDrawer
          onClose={() => setIpDrawer(null)}
          eventIp={ipDrawer.eventIp}
          windows={ipDrawer.windows}
        />
      )}
    </div>
  );
}
